import {
    activeDataTableFilters,
    type VxDataTableColumn,
    type VxDataTableFilter,
    type VxDataTableFilterField,
    type VxDataTableFilterOperatorOptions,
    type VxDataTableFilterSelectable,
} from '@voxie/frontend-components';
import {
    isRunStatus,
    notCheckedRows,
    reassignedRows,
    runStatusOptions,
    type RndCountRow,
} from '~/components/kiosk/kiosk-reassigned-numbers/rndLabels';
import type { RndRun, RndRunsParams } from '~/services/adminRndClient.types';

const RND_RUNS_PER_PAGE = 25;
export const RND_RUNS_MAX_FILTER_VALUES = 10;

export interface KioskRndHistoryRow extends RndRun {
    queried: number | null;
    reassigned: number | null;
    reassigned_rows: RndCountRow[];
    no_data: number | null;
    not_checked: number | null;
    not_checked_rows: RndCountRow[];
}

export type KioskRndHistoryFilter = VxDataTableFilter<KioskRndHistoryRow>;

export const kioskRndHistoryColumns: VxDataTableColumn<KioskRndHistoryRow>[] = [
    { key: 'status', label: 'Status', width: 260 },
    { key: 'team_id', label: 'Team', width: 220 },
    { key: 'company_id', label: 'RND Company ID', width: 140 },
    { key: 'user_id', label: 'Run by', width: 190 },
    { key: 'trigger', label: 'Type', width: 230 },
    { key: 'queried', label: 'Queried', width: 110, align: 'end' },
    { key: 'reassigned', label: 'Reassigned', width: 120, align: 'end' },
    { key: 'no_data', label: 'No data', width: 100, align: 'end' },
    { key: 'not_checked', label: 'Not checked', width: 120, align: 'end' },
    { key: 'snapshot_at', label: 'Started', width: 200 },
    { key: 'finished_at', label: 'Finished', width: 200 },
];

const inOnly: VxDataTableFilterOperatorOptions = [{ value: 'in', label: 'In', chipLabel: 'in' }];

export const kioskRndHistoryFilterFields = (
    teamSelectable: VxDataTableFilterSelectable
): VxDataTableFilterField<KioskRndHistoryRow>[] => [
    { key: 'team_id', label: 'Team', selectable: teamSelectable, operators: inOnly },
    { key: 'status', label: 'Status', values: runStatusOptions, operators: inOnly },
];

export const toHistoryRow = (run: RndRun): KioskRndHistoryRow => ({
    ...run,
    queried: run.counts?.queried ?? null,
    reassigned: run.counts?.reassigned ?? null,
    reassigned_rows: run.counts === null ? [] : reassignedRows(run.counts.reassigned_breakdown),
    no_data: run.counts?.no_data ?? null,
    not_checked: run.counts?.not_checked ?? null,
    not_checked_rows: run.counts === null ? [] : notCheckedRows(run.counts.not_checked_breakdown),
});

const filterValues = (filters: KioskRndHistoryFilter[], key: 'status' | 'team_id'): string[] =>
    activeDataTableFilters(filters)
        .filter((filter) => filter.key === key && filter.operator === 'in')
        .flatMap((filter) => filter.values);

export const toRunsParams = (filters: KioskRndHistoryFilter[]): RndRunsParams => {
    const teamIds = filterValues(filters, 'team_id').map(Number);
    const statuses = filterValues(filters, 'status').filter(isRunStatus);

    return {
        ...(teamIds.length === 0 ? {} : { team_id: teamIds }),
        ...(statuses.length === 0 ? {} : { status: statuses }),
        per_page: RND_RUNS_PER_PAGE,
    };
};

export const isNarrowed = (params: RndRunsParams): boolean =>
    params.team_id !== undefined || params.status !== undefined;

export const exceedsFilterValueLimit = (filters: KioskRndHistoryFilter[]): boolean =>
    filters.some((filter) => filter.values.length > RND_RUNS_MAX_FILTER_VALUES);

export const nextCursorFrom = (nextPageUrl: string | null): string | null =>
    nextPageUrl === null ? null : new URLSearchParams(nextPageUrl.split('?')[1]).get('cursor');
